/**
 * Events — a flat list over the pipeline's WorkItem table. Every event type
 * renders identically (type, title, status, timestamps); clicking a row
 * expands the raw event (payload / result / error) inline. Built to be
 * SCANNED for exceptions (failed, suspended) among thousands of
 * self-resolving events — not read row by row. No per-type UI, no detail page.
 */
import { Fragment, useState } from 'react';
import { Check, ChevronRight, Copy } from '@servicecompressionllc/sc-design-system/icons';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Input,
  IconButton,
  Select,
  Skeleton,
  Table,
  type BadgeVariant,
} from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { EventCatalogTab } from './EventCatalogTab.js';
import { EventReviewTab } from './EventReviewTab.js';
import { EventBlockedTab } from './EventBlockedTab.js';
import { useEventCatalogLogic, useEventCatalogMutations } from './useEventCatalogLogic.js';
import { useNavigate } from 'react-router-dom';
import { Pagination } from '../../components/Pagination.js';
import { useMe } from '../../hooks/useMe.js';
import { usePagination } from '../../hooks/usePagination.js';
import type {
  EventsTab,
  EventsView,
  PipelineEvent,
  PipelineEventStatus,
  TenantEventType,
} from '../../lib/types.js';
import { EventRuns } from './EventRuns.js';
import { useEventDetailLogic, useEventsListLogic } from './useEventsLogic.js';

export const STATUS_LABELS: Record<PipelineEventStatus, string> = {
  pending: 'pending',
  running: 'running',
  done: 'done',
  failed: 'failed',
  handed_off: 'suspended',
};

export const statusBadgeVariant = (status: PipelineEventStatus): BadgeVariant => {
  switch (status) {
    case 'pending':
    case 'running':
      return 'info';
    case 'handed_off':
      return 'warning';
    case 'failed':
      return 'danger';
    case 'done':
      return 'success';
  }
};

/** "4h" / "3d" / "12m" — `now` is a parameter for testability. */
export const formatAge = (iso: string, now: number = Date.now()): string => {
  const minutes = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

const PAGE_SIZE = 25;

const VIEWS: { value: EventsView; label: string }[] = [
  { value: 'open', label: 'View: Open' },
  { value: 'failed', label: 'Failed only' },
  { value: 'all', label: 'All (incl. done)' },
];

const RawRecord = ({ tenantId, eventId }: { tenantId: string; eventId: string }) => {
  const detail = useEventDetailLogic(tenantId, eventId);
  const [copied, setCopied] = useState(false);
  if (detail.isLoading) return <Skeleton className="h-24 w-full rounded-lg" />;
  if (detail.error) {
    return <p className="text-ink-muted text-sm">Record unavailable ({detail.error.message}).</p>;
  }
  const json = JSON.stringify(detail.data, null, 2);
  return (
    <div className="relative">
      {/* Wrapped and scrolled INSIDE the well: unwrapped JSON grew the row to
          the widest line and put the horizontal scrollbar on the whole table. */}
      <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-outline bg-surface p-3 pr-10 text-xs text-ink">
        {json}
      </pre>
      <IconButton
        aria-label="Copy raw event"
        variant="ghost"
        className="absolute right-2 top-2"
        onClick={() => {
          void navigator.clipboard.writeText(json).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </IconButton>
    </div>
  );
};

export const EventsPage = () => {
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;

  // Filters live in the URL so a filtered view is a shareable link (and the
  // Workflows page's unmatched counter can deep-link here). Ephemeral UI
  // state (expanded row) stays local.
  const [params, setParams] = useSearchParams();
  const view = (params.get('view') as EventsView | null) ?? 'all';
  const typeFilter = params.get('type') ?? 'all';
  const search = params.get('q') ?? '';
  const unrouted = params.get('unrouted') === 'true';
  const setParam = (key: string, value: string, defaultValue: string) => {
    setParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        if (value === defaultValue) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };
  const setView = (v: EventsView) => setParam('view', v, 'all');
  const setTypeFilter = (v: string) => setParam('type', v, 'all');
  const setSearch = (v: string) => setParam('q', v, '');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The tab is a URL param so a Needs-review link is shareable; `activity`
  // is the default and therefore stays OUT of the URL.
  const requestedTab = (params.get('tab') as EventsTab | null) ?? 'activity';
  const setTab = (t: EventsTab) => setParam('tab', t, 'activity');

  const catalog = useEventCatalogLogic(tenantId);
  const catalogMutations = useEventCatalogMutations(tenantId);

  // Blocked earns a tab only once something is blocked, which is rarely and
  // maybe never. Falling back matters because the tab can disappear under you:
  // re-register the last blocked type and this is the tab you are standing on.
  // Gated on the catalog having loaded, or a deep link would flash to Catalog
  // while the list is still in flight.
  const showBlockedTab = catalog.blocked.length > 0;
  const tab: EventsTab =
    requestedTab === 'blocked' && !showBlockedTab && !catalog.isLoading ? 'catalog' : requestedTab;
  const navigate = useNavigate();
  const openEditor = (entry: TenantEventType | null) => {
    void navigate(entry ? `/events/catalog/${entry.id}/edit` : '/events/register');
  };

  const eventsQuery = useEventsListLogic(tenantId, view, unrouted, tab === 'activity');
  const allEvents = eventsQuery.data?.events ?? [];
  const filtered = allEvents.filter((event: PipelineEvent) => {
    if (typeFilter !== 'all' && event.type !== typeFilter) return false;
    const needle = search.trim().toLowerCase();
    return needle.length === 0 || `${event.type} ${event.title}`.toLowerCase().includes(needle);
  });
  const pager = usePagination(filtered, PAGE_SIZE);

  // Only the Activity tab waits on the events list — the other two would sit
  // behind a skeleton for a query they never use.
  if (meQuery.isLoading || !tenantId || (tab === 'activity' && eventsQuery.isLoading)) {
    return (
      <AppShell>
        <Skeleton className="mb-2 h-7 w-48" />
        <Skeleton className="mb-6 h-4 w-96" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </AppShell>
    );
  }

  const types = ['all', ...new Set(allEvents.map((e) => e.type))];
  const rows = pager.pageItems;

  return (
    // `fill` makes the shell a viewport-height flex column, which Activity
    // wants: its table scrolls inside its own container. The catalog tabs are
    // ordinary flowing documents, so they take the standard shell and its
    // pb-12 instead of running off the bottom of the page.
    <AppShell fill={tab === 'activity'}>
      {/* Registering is a page-level action, so it lives in the header rather
          than inside the tab bar. It used to render only on Catalog, which
          changed the bar's height and made the tabs jump between tabs. */}
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h2 className="text-ink text-2xl font-bold">Events</h2>
          {unrouted && (
            <p className="mt-1 text-sm text-warning">
              Showing only events no enabled workflow listened to.{' '}
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => setParam('unrouted', 'false', 'false')}
              >
                Show all
              </button>
              <span className="text-ink-muted"> · </span>
              <Link to="/settings/workflows/templates" className="text-primary hover:underline">
                Start a workflow for these
              </Link>
            </p>
          )}
          {/* Two lines' worth, always: the per-tab copy is a different length
              and would otherwise shift the tabs by a line. */}
          <p className="text-ink-muted mt-1.5 min-h-10 max-w-[72ch] text-sm">
            {tab === 'activity'
              ? 'Everything flowing through the pipeline. Events resolve themselves, so scan for the failed and suspended ones. Click a row for its raw event.'
              : tab === 'blocked'
                ? 'Types you have refused. Their senders get an error, and nothing about them is recorded.'
                : 'The event types this tenant accepts. Anything not on this list is held instead of processed, so register it here to let workflows use it.'}
          </p>
        </div>
        {/* Registering belongs to the catalog surfaces. Activity's job is
            scanning for failures, so the loudest control hides there — the
            title block is taller than the button, so no layout jump. */}
        {tab !== 'activity' && (
          <Button className="mt-1 flex-shrink-0" onClick={() => openEditor(null)}>
            Register event
          </Button>
        )}
      </div>

      <div className="border-outline mb-5 mt-4 flex items-center gap-6 border-b">
        {(
          [
            ['activity', 'Activity', 0],
            ['catalog', 'Catalog', 0],
            ['review', 'Needs review', catalog.held.length],
            ...(showBlockedTab
              ? ([['blocked', 'Blocked', catalog.blocked.length]] as [EventsTab, string, number][])
              : []),
          ] as [EventsTab, string, number][]
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px flex items-center gap-2 border-b-2 pb-2.5 text-sm ${
              tab === key
                ? 'border-primary text-ink font-semibold'
                : 'text-ink-muted border-transparent'
            }`}
          >
            {label}
            {count > 0 && (
              <span className="bg-surface-hover border-outline text-ink-muted flex h-[18px] min-w-[18px] items-center justify-center rounded-full border px-1.5 text-[11px] font-semibold">
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {catalog.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{catalog.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {tab === 'catalog' && (
        <EventCatalogTab
          approved={catalog.approved}
          heldCount={catalog.held.length}
          onReviewHeld={() => setTab('review')}
          onRegisterNew={() => openEditor(null)}
          onEdit={openEditor}
          onDisable={(e) => void catalogMutations.disable(e.id)}
          onBlock={(e) => void catalogMutations.block(e.id)}
        />
      )}
      {tab === 'review' && (
        <EventReviewTab
          held={catalog.held}
          isBusy={catalogMutations.isBusy}
          onRegister={openEditor}
          onDismiss={(e) => void catalogMutations.dismiss(e.id)}
          onBlock={(e) => void catalogMutations.block(e.id)}
        />
      )}
      {tab === 'blocked' && <EventBlockedTab blocked={catalog.blocked} onUnblock={openEditor} />}

      {eventsQuery.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{eventsQuery.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {tab === 'activity' && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Select
              aria-label="View"
              className="w-44"
              searchable={false}
              options={VIEWS.map((v) => ({ value: v.value, label: v.label }))}
              value={[view]}
              onChange={(v) => setView((v[0] ?? 'all') as EventsView)}
            />
            <Select
              aria-label="Type"
              className="w-52"
              searchable={false}
              options={types.map((t) => ({ value: t, label: t === 'all' ? 'Type: All' : t }))}
              value={[typeFilter]}
              onChange={(v) => setTypeFilter(v[0] ?? 'all')}
            />
            <div className="min-w-[160px] flex-1">
              <Input
                type="search"
                aria-label="Search events"
                placeholder="Search events…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="min-h-40 overflow-auto rounded-xl border border-outline bg-surface-elevated">
            <Table.Root className="w-full min-w-[52rem] text-sm">
              <Table.Header sticky>
                <Table.Row>
                  <Table.HeaderCell className="w-8" aria-label="Expand" />
                  <Table.HeaderCell>Type</Table.HeaderCell>
                  <Table.HeaderCell>Event</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell align="right">Created</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((event) => (
                  <Fragment key={event.id}>
                    <Table.Row
                      className="cursor-pointer hover:bg-surface-hover"
                      onClick={() => setExpandedId(expandedId === event.id ? null : event.id)}
                    >
                      <Table.Cell className="w-8 pr-0">
                        <ChevronRight
                          size={14}
                          aria-hidden
                          className={`text-ink-subtle transition-transform ${
                            expandedId === event.id ? 'rotate-90' : ''
                          }`}
                        />
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        <Badge variant="neutral">{event.type}</Badge>
                      </Table.Cell>
                      <Table.Cell className="text-ink">{event.title}</Table.Cell>
                      <Table.Cell>
                        <Badge variant={statusBadgeVariant(event.status)}>
                          {STATUS_LABELS[event.status]}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell
                        align="right"
                        className="text-ink-muted whitespace-nowrap tabular-nums"
                      >
                        {formatAge(event.createdAt)}
                      </Table.Cell>
                    </Table.Row>
                    {expandedId === event.id && (
                      <Table.Row key={`${event.id}-drawer`}>
                        <Table.Cell colSpan={5} className="bg-surface">
                          <div className="grid gap-4 py-1">
                            {/* What it SET OFF leads; the raw event is the fallback
                              for when the rendered view doesn't answer the question. */}
                            <EventRuns tenantId={tenantId} eventId={event.id} />
                            <details className="group">
                              <summary className="cursor-pointer text-xs text-ink-subtle hover:text-ink-muted">
                                Raw event
                              </summary>
                              <div className="mt-2">
                                <RawRecord tenantId={tenantId} eventId={event.id} />
                              </div>
                            </details>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Fragment>
                ))}
                {rows.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={5} className="text-ink-muted py-8 text-center">
                      No events match the current filters.
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>
          </div>
          <Pagination
            className="mt-3"
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
          />
        </>
      )}
    </AppShell>
  );
};
